// sRGB <-> OKLab / OKLCH (Björn Ottosson, https://bottosson.github.io/posts/oklab/)
// for glyph.swatch@1: a flat swatch whose `tone` is an OKLCH lightness offset on
// the token colour. Differences down to 0.015 must stay distinct after 8-bit
// quantisation, so the maths runs in float64 and only rounds at the very end.
//
// Out-of-gamut results are mapped back by reducing chroma at constant L and hue
// (CSS Color 4 style), never by clipping channels, so a tone step always moves
// lightness instead of collapsing at a channel ceiling.

import { PUZZLE_TOKENS } from "./palette.generated";
import type { PuzzleColor } from "./types";

export type Rgb = readonly [number, number, number]; // 0..1, gamma-encoded sRGB
export type Oklab = readonly [number, number, number]; // L 0..1, a, b
export type Oklch = readonly [number, number, number]; // L 0..1, C >= 0, h degrees

// ---------------------------------------------------------------- hex

/** "#RRGGBB" or "#RRGGBBAA" → rgb 0..1 plus alpha 0..1. */
export function parseHex(hex: string): { rgb: Rgb; alpha: number } {
  const m = /^#?([0-9a-f]{6})([0-9a-f]{2})?$/i.exec(hex.trim());
  if (!m) throw new Error(`Not a hex colour: ${hex}`);
  const n = Number.parseInt(m[1]!, 16);
  const alpha = m[2] ? Number.parseInt(m[2], 16) / 255 : 1;
  return { rgb: [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255], alpha };
}

const byte = (v: number) => Math.round(Math.min(1, Math.max(0, v)) * 255);

/** rgb 0..1 → "#RRGGBB" (8-bit, rounded). */
export function toHex(rgb: Rgb): string {
  return `#${rgb.map((v) => byte(v).toString(16).padStart(2, "0")).join("").toUpperCase()}`;
}

// ---------------------------------------------------------------- transfer functions

const toLinear = (c: number) => (c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
const toGamma = (c: number) => (c <= 0.0031308 ? 12.92 * c : 1.055 * Math.sign(c) * Math.abs(c) ** (1 / 2.4) - 0.055);

// ---------------------------------------------------------------- OKLab

export function srgbToOklab([r, g, b]: Rgb): Oklab {
  const lr = toLinear(r);
  const lg = toLinear(g);
  const lb = toLinear(b);
  const l = Math.cbrt(0.4122214708 * lr + 0.5363325363 * lg + 0.0514459929 * lb);
  const m = Math.cbrt(0.2119034982 * lr + 0.6806995451 * lg + 0.1073969566 * lb);
  const s = Math.cbrt(0.0883024619 * lr + 0.2817188376 * lg + 0.6299787005 * lb);
  return [
    0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s,
    1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s,
    0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s,
  ];
}

/** OKLab → linear-light sRGB (may be out of gamut). */
function oklabToLinear([L, a, b]: Oklab): Rgb {
  const l = (L + 0.3963377774 * a + 0.2158037573 * b) ** 3;
  const m = (L - 0.1055613458 * a - 0.0638541728 * b) ** 3;
  const s = (L - 0.0894841775 * a - 1.291485548 * b) ** 3;
  return [
    4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
    -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
    -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s,
  ];
}

/** OKLab → gamma-encoded sRGB, unclamped (check with `inGamut`). */
export function oklabToSrgb(lab: Oklab): Rgb {
  const [r, g, b] = oklabToLinear(lab);
  return [toGamma(r), toGamma(g), toGamma(b)];
}

export function oklabToOklch([L, a, b]: Oklab): Oklch {
  const C = Math.hypot(a, b);
  const h = C < 1e-9 ? 0 : ((Math.atan2(b, a) * 180) / Math.PI + 360) % 360;
  return [L, C, h];
}

export function oklchToOklab([L, C, h]: Oklch): Oklab {
  const rad = (h * Math.PI) / 180;
  return [L, C * Math.cos(rad), C * Math.sin(rad)];
}

const EPS = 1e-6;
export const inGamut = (rgb: Rgb) => rgb.every((v) => v >= -EPS && v <= 1 + EPS);

/**
 * OKLCH → sRGB, mapped into gamut by lowering chroma (binary search) at fixed
 * lightness and hue. L is clamped to 0..1 first.
 */
export function oklchToSrgbInGamut([L0, C0, h]: Oklch): Rgb {
  const L = Math.min(1, Math.max(0, L0));
  if (L >= 1 - EPS) return [1, 1, 1];
  if (L <= EPS) return [0, 0, 0];
  const direct = oklabToSrgb(oklchToOklab([L, C0, h]));
  if (inGamut(direct)) return clamp01(direct);
  let lo = 0;
  let hi = C0;
  for (let i = 0; i < 32; i++) {
    const mid = (lo + hi) / 2;
    if (inGamut(oklabToSrgb(oklchToOklab([L, mid, h])))) lo = mid;
    else hi = mid;
  }
  return clamp01(oklabToSrgb(oklchToOklab([L, lo, h])));
}

const clamp01 = (rgb: Rgb): Rgb => [0, 1, 2].map((i) => Math.min(1, Math.max(0, rgb[i]!))) as unknown as Rgb;

// ---------------------------------------------------------------- tone offsets

/** Shifts a hex colour's OKLCH lightness by `tone` (−1..1) and returns #RRGGBB (alpha kept as #RRGGBBAA). */
export function shiftTone(hex: string, tone: number): string {
  const { rgb, alpha } = parseHex(hex);
  const [L, C, h] = oklabToOklch(srgbToOklab(rgb));
  const t = Number.isFinite(tone) ? tone : 0;
  const out = toHex(oklchToSrgbInGamut([L + t, C, h]));
  return alpha < 1 ? `${out}${byte(alpha).toString(16).padStart(2, "0").toUpperCase()}` : out;
}

const SWATCH_COLORS = new Set<string>([
  "ink",
  "accent",
  "ember",
  "violet",
  "mint",
  "sky",
  "pink",
  "amber",
  "orange",
  "red",
  "blue",
  "purple",
  "yellow",
  "green",
  "muted",
]);

/** Token hex for a semantic puzzle colour (unknown names fall back to ink). */
export function tokenHex(color: PuzzleColor | string | undefined): string {
  const key = color && SWATCH_COLORS.has(color) ? color : "ink";
  return PUZZLE_TOKENS[key as keyof typeof PUZZLE_TOKENS];
}

/** Clamp for glyph.swatch@1 `tone` (schema range −0.5…0.5). */
export const clampTone = (tone: unknown) =>
  typeof tone === "number" && Number.isFinite(tone) ? Math.min(0.5, Math.max(-0.5, tone)) : 0;

/** The colour a glyph.swatch@1 renders with. */
export function swatchHex(color: PuzzleColor | string | undefined, tone: unknown): string {
  return shiftTone(tokenHex(color), clampTone(tone));
}

// ---------------------------------------------------------------- iso face shading

/**
 * The violet voxel tokens (voxelTop / voxelLeft / voxelRight) expressed as OKLCH
 * lightness offsets from `color.puzzle.violet`, so a tinted glyph.iso@1 gets the
 * same top / left / right shading as the default violet stack.
 */
export const ISO_FACE_TONES = (() => {
  const base = oklabToOklch(srgbToOklab(parseHex(PUZZLE_TOKENS.violet).rgb))[0];
  const L = (hex: string) => oklabToOklch(srgbToOklab(parseHex(hex).rgb))[0];
  return {
    top: L(PUZZLE_TOKENS.voxelTop) - base,
    left: L(PUZZLE_TOKENS.voxelLeft) - base,
    right: L(PUZZLE_TOKENS.voxelRight) - base,
  } as const;
})();

export type IsoFaceName = keyof typeof ISO_FACE_TONES;

/** Fill for one iso face: the voxel tokens for violet (or no colour), tone-shifted token colour otherwise. */
export function isoFaceHex(color: PuzzleColor | string | undefined, face: IsoFaceName): string {
  if (!color || color === "violet" || !SWATCH_COLORS.has(color)) {
    return face === "top" ? PUZZLE_TOKENS.voxelTop : face === "left" ? PUZZLE_TOKENS.voxelLeft : PUZZLE_TOKENS.voxelRight;
  }
  return shiftTone(tokenHex(color).slice(0, 7), ISO_FACE_TONES[face]);
}
